/**
 * Centralised API client built on axios (PX-W6.1) — the ONE transport every
 * feature service goes through (boundary-enforced by
 * `src/test/transportBoundary.test.ts`).
 *
 * Ported from xelerate-ai's `services/apiClient.ts` with three px deltas:
 * the error envelope is `{code:int, error:key, message}` (px `ErrorBodies`),
 * the active-org id comes from a registered getter backed by the session
 * slice (not web storage), and the session-expired bounce targets
 * `/login?reason=session-expired`.
 *
 * Features:
 * - Base URL from `VITE_API_BASE_URL` (fallback: dev `localhost:9112/api/v1`,
 *   prod same-origin `/api/v1`)
 * - Bearer injection from the in-memory token store (never web storage)
 * - `withCredentials: true` so the browser attaches the httpOnly
 *   `refreshToken` cookie on every call
 * - `{orgId}` URL placeholder replacement from the registered active-org getter
 * - Response unwrapping (returns `response.data` directly)
 * - 401 → ONE shared silent refresh (`POST /auth/refresh`) → retry once;
 *   refresh failure or a retried-401 → a single latched session-expired
 *   redirect to `/login?reason=session-expired`
 * - Auth endpoints are excluded from silent refresh (a wrong password is an
 *   inline error, never a bounce)
 * - Every rejection surfaces the canonical `ApiError` (px envelope fields)
 *
 * @example
 * ```ts
 * import { apiClient } from '@/services'
 *
 * const user = await apiClient.get<SessionUser>('/auth/me')
 * ```
 */

import axios from 'axios'
import type {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  InternalAxiosRequestConfig
} from 'axios'

import { getApiBaseUrl } from './apiBaseUrl'
import { apiErrorFromBody, toApiError, type ApiError } from './apiError'
import { createSseParser, type SseFrame } from './sseFrames'

/**
 * Endpoints whose 401 means "the credential you just typed is wrong" (or the
 * refresh cookie is dead) — never "your session expired mid-flight". Without
 * this exclusion a wrong password would silently refresh, retry, 401 again and
 * bounce the user to /login instead of surfacing the inline error.
 */
// TASK-714FBFBD (R.5.4): a 401 on the SSO code exchange is a spent/expired code for /auth/callback's alert, never a session expiry.
const EXCLUDED_AUTH_PATHS = ['/auth/login', '/auth/refresh', '/auth/register', '/auth/logout', '/auth/oauth/exchange'] as const

/** Marker on a request config that has already been retried after a refresh. */
interface RetriedConfig extends InternalAxiosRequestConfig {
  _retried?: boolean
}

/** Options of {@link ApiClient.postEventStream}. */
export interface EventStreamOptions {
  /** Called once per complete `text/event-stream` frame, in arrival order. */
  onFrame: (frame: SseFrame) => void
  /** Aborting it cancels the request (before the response) or the stream (mid-read). */
  signal?: AbortSignal
}

/** A non-OK streaming response as the canonical {@link ApiError}, from its JSON envelope when it has one. */
async function streamError(response: Response): Promise<ApiError> {
  let body: unknown = {}
  try {
    const text = await response.text()
    body = text === '' ? {} : (JSON.parse(text) as unknown)
  } catch {
    // Not JSON (or unreadable): the status and the fallback message carry it.
  }
  return apiErrorFromBody(response.status, body, response.statusText || `Request failed with status code ${response.status}`)
}

// ---------------------------------------------------------------------------
// Token + org stores — registered by the auth service / the store, so this
// module never imports them (no circular import at module-init time).
// ---------------------------------------------------------------------------

let _getAccessToken: (() => string | null) | null = null
let _setAccessToken: ((token: string) => void) | null = null
let _clearAccessToken: (() => void) | null = null
let _getActiveOrgId: (() => string | null) | null = null

/**
 * Register the in-memory token store (called by `authService.http.ts` at
 * import time).
 *
 * @param getter  - Returns the current in-memory access token.
 * @param setter  - Stores a new access token in memory.
 * @param clearer - Wipes the in-memory access token.
 */
export function registerTokenStore(
  getter: () => string | null,
  setter: (token: string) => void,
  clearer: () => void
): void {
  _getAccessToken = getter
  // Wrap the setter so a fresh token (login / successful refresh) clears the
  // session-expired latch — otherwise the next legitimate 401 after re-login
  // would be silently swallowed by the singleton's latched state.
  _setAccessToken = (token: string) => {
    apiClient.resetSessionExpired()
    setter(token)
  }
  _clearAccessToken = clearer
}

/**
 * Register the active-organisation getter (called from `store/index.ts`; the
 * answer is backed by the session slice — decision D7).
 *
 * @param getter - Returns the active org id, or null when there is none.
 */
export function registerActiveOrgId(getter: () => string | null): void {
  _getActiveOrgId = getter
}

// ---------------------------------------------------------------------------
// ApiClient class
// ---------------------------------------------------------------------------

/**
 * Typed HTTP client wrapping an axios instance. All public methods return
 * `Promise<T>` (the response body) after the interceptor unwrapped
 * `response.data`.
 */
export class ApiClient {
  private readonly instance: AxiosInstance
  /** Tracks an in-flight refresh call so concurrent 401s share ONE refresh. */
  private refreshPromise: Promise<string> | null = null
  /**
   * Latches once the session-expired redirect has fired so N concurrent 401s
   * whose shared refresh rejects produce ONE bounce, not N.
   */
  private sessionExpiredHandled = false

  /**
   * @param config - Optional extra axios config (tests inject a scripted
   *   `adapter`; nothing else should need it).
   */
  constructor(config: AxiosRequestConfig = {}) {
    this.instance = axios.create({
      baseURL: getApiBaseUrl(),
      timeout: 30_000,
      // Sends the httpOnly refresh cookie on every request.
      withCredentials: true,
      headers: { 'Content-Type': 'application/json' },
      ...config
    })
    this.setupRequestInterceptor()
    this.setupResponseInterceptor()
  }

  async get<T>(path: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.get<T>(path, config)
    return response.data
  }

  async post<T>(path: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.post<T>(path, data, config)
    return response.data
  }

  async put<T>(path: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.put<T>(path, data, config)
    return response.data
  }

  async patch<T>(path: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.patch<T>(path, data, config)
    return response.data
  }

  async delete<T>(path: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.instance.delete<T>(path, config)
    return response.data
  }

  /**
   * POST a JSON body and read the `text/event-stream` answer frame by frame
   * (CR-BUILD-BOARD-EPICS-2026-09-26 — ticket generation, the first streaming
   * consumer on this transport). axios cannot hand a browser a readable
   * stream, so this one method uses the platform's own request API — still
   * inside `src/services`, still with the same Bearer, cookie, `{orgId}`
   * placeholder and base URL as every other call.
   *
   * A 401 takes the SAME path as the axios interceptor: one shared silent
   * refresh, one retry; a failed refresh or a second 401 latches the
   * session-expired bounce. A non-OK status (a refusal before the stream
   * opens) rejects with the canonical {@link ApiError} and delivers no frame.
   * Resolves when the server closes the stream; rejects with the abort error
   * when `signal` aborts.
   *
   * @param path    - Path under the API base, `{orgId}` placeholders allowed.
   * @param body    - JSON body (`{}` when omitted).
   * @param options - The frame callback and an optional abort signal.
   */
  async postEventStream(path: string, body: unknown, options: EventStreamOptions): Promise<void> {
    const orgId = _getActiveOrgId?.() ?? null
    const resolvedPath = orgId ? path.replace(/\{orgId\}/g, orgId) : path
    const url = `${this.instance.defaults.baseURL ?? getApiBaseUrl()}${resolvedPath}`
    const send = (token: string | null): Promise<Response> =>
      globalThis.fetch(url, {
        method: 'POST',
        credentials: 'include',
        signal: options.signal,
        headers: {
          Accept: 'text/event-stream',
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify(body ?? {})
      })

    let response = await send(_getAccessToken?.() ?? null)
    if (response.status === 401) {
      let refreshed: string
      try {
        refreshed = await this.refreshAccessToken()
      } catch {
        this.handleSessionExpired()
        throw await streamError(response)
      }
      response = await send(refreshed)
      if (response.status === 401) this.handleSessionExpired()
    }
    if (!response.ok || response.body === null) throw await streamError(response)

    const reader = response.body.getReader()
    const decoder = new TextDecoder('utf-8')
    const parser = createSseParser(options.onFrame)
    try {
      for (;;) {
        const { value, done } = await reader.read()
        if (done) break
        parser.push(decoder.decode(value, { stream: true }))
      }
      parser.push(decoder.decode())
      parser.flush()
    } finally {
      await reader.cancel().catch(() => undefined)
    }
  }

  // -------------------------------------------------------------------------
  // Session lifecycle
  // -------------------------------------------------------------------------

  /**
   * Wipe the in-memory token and bounce to `/login?reason=session-expired`.
   * Latched so N concurrent 401s only produce one navigation. Called when (a)
   * the silent refresh fails or (b) the retried request still returns 401.
   */
  private handleSessionExpired(): void {
    if (this.sessionExpiredHandled) {
      return
    }
    this.sessionExpiredHandled = true
    _clearAccessToken?.()
    if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
      window.location.assign('/login?reason=session-expired')
    }
  }

  /**
   * Clear the session-expired latch so a future 401 (after re-login) can fire
   * the bounce path again. Invoked from the wrapped token setter registered
   * via {@link registerTokenStore}.
   */
  resetSessionExpired(): void {
    this.sessionExpiredHandled = false
  }

  // -------------------------------------------------------------------------
  // Token refresh
  // -------------------------------------------------------------------------

  /**
   * Call `POST /auth/refresh` for a new access token. The refresh token rides
   * the httpOnly cookie. Concurrent 401s share the single in-flight promise
   * (thundering-herd guard); the promise clears itself once settled.
   *
   * @returns The new access token string.
   */
  private refreshAccessToken(): Promise<string> {
    if (this.refreshPromise) {
      return this.refreshPromise
    }
    this.refreshPromise = this.instance
      .post<{ accessToken: string }>('/auth/refresh')
      .then((res) => {
        const { accessToken } = res.data
        if (typeof accessToken !== 'string' || accessToken === '') {
          throw new Error('refresh response carried no accessToken')
        }
        _setAccessToken?.(accessToken)
        return accessToken
      })
      .finally(() => {
        this.refreshPromise = null
      })
    return this.refreshPromise
  }

  // -------------------------------------------------------------------------
  // Interceptors
  // -------------------------------------------------------------------------

  /** Inject the Bearer token and replace `{orgId}` placeholders. */
  private setupRequestInterceptor(): void {
    this.instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
      const token = _getAccessToken?.() ?? null
      if (token) {
        config.headers.set('Authorization', `Bearer ${token}`)
      }
      const orgId = _getActiveOrgId?.() ?? null
      if (orgId && config.url) {
        config.url = config.url.replace(/\{orgId\}/g, orgId)
      }
      return config
    })
  }

  /**
   * On 401 (non-auth endpoint, not yet retried): one shared silent refresh,
   * then retry the original request ONCE with the new Bearer. A failed
   * refresh, or a 401 on the retry, is a hard session expiry.
   */
  private setupResponseInterceptor(): void {
    this.instance.interceptors.response.use(
      (response) => response,
      async (error: AxiosError<Record<string, unknown>>) => {
        const originalRequest = error.config as RetriedConfig | undefined
        const apiError = toApiError(error)
        const isAuthEndpoint =
          originalRequest?.url !== undefined &&
          EXCLUDED_AUTH_PATHS.some((path) => originalRequest.url?.includes(path))

        if (apiError.status === 401 && originalRequest && !isAuthEndpoint) {
          if (!originalRequest._retried) {
            originalRequest._retried = true
            try {
              const newToken = await this.refreshAccessToken()
              originalRequest.headers.set('Authorization', `Bearer ${newToken}`)
              return this.instance(originalRequest)
            } catch {
              // Refresh failed — the session is truly expired.
              this.handleSessionExpired()
            }
          } else {
            // The retry after a successful refresh STILL returned 401 — the
            // new token is not trusted (revoked / wrong org). Hard expiry.
            this.handleSessionExpired()
          }
        }

        return Promise.reject(apiError)
      }
    )
  }
}

// ---------------------------------------------------------------------------
// Singleton export
// ---------------------------------------------------------------------------

/** The ONE transport. Import from `@/services` and never build another. */
export const apiClient = new ApiClient()
